import React, { useState } from 'react';
import { Upload, Button, message, Modal, Divider, Space, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Text } = Typography;
const MAX_VIDEO_MB = 50;
const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

const parseUploadResponse = async (response) => {
  const raw = await response.text();
  if (!raw) {
    return {
      errorMessage: response.ok ? null : `Upload failed (${response.status})`,
      payload: null,
    };
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      errorMessage: response.ok ? null : (parsed?.error || parsed?.message || `Upload failed (${response.status})`),
      payload: parsed,
    };
  } catch {
    return {
      errorMessage: response.ok ? null : raw,
      payload: null,
    };
  }
};

const VideoUploader = ({ token, onUploaded }) => {
  const [uploading, setUploading] = useState(false);

  const showRestrictionModal = () => {
    const mail = 'info@platypus360.com';
    Modal.error({
      title: 'Upload Restriction',
      content: (
        <div style={{ paddingTop: 8 }}>
          <Text>Videos uploaded here must be {MAX_VIDEO_MB}MB or smaller.</Text>

          <Divider />

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text>If your file is larger than {MAX_VIDEO_MB}MB, please contact us via email:</Text>
            <Text
              copyable={{ text: mail }}
              code
              style={{ fontSize: '1.1rem', display: 'block', textAlign: 'center' }}
            >
              {mail}
            </Text>
          </Space>
        </div>
      ),
      okText: 'Got it',
    });
  };

  const beforeUpload = (file) => {
    const isVideo = ALLOWED_VIDEO_MIME_TYPES.includes(file.type);
    if (!isVideo) {
      message.error('Only MP4, MOV, WEBM formats are supported!');
      return Upload.LIST_IGNORE;
    }

    const isWithinLimit = file.size / 1024 / 1024 <= MAX_VIDEO_MB;
    if (!isWithinLimit) {
      message.error(`Video must be smaller than ${MAX_VIDEO_MB}MB!`);
      showRestrictionModal();
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', file);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/video/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const { errorMessage, payload } = await parseUploadResponse(res);
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (!payload?.fileUrl) {
        throw new Error('Upload succeeded but no file URL was returned');
      }

      message.success('Upload successful');
      onUploaded?.(payload.fileUrl);
      onSuccess?.();
    } catch (err) {
      console.error('[Upload Video Error]', err);
      message.error(err?.message || 'Upload failed');
      onError?.(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Upload
      accept=".mp4,.mov,.webm"
      showUploadList={false}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
    >
      <Button icon={<UploadOutlined />} loading={uploading}>
        {`Upload Video (<= ${MAX_VIDEO_MB}MB)`}
      </Button>
    </Upload>
  );
};

export default VideoUploader;
